<template>
  <Transition name="push-optin">
    <div
      v-if="visible"
      class="fixed bottom-4 left-1/2 -translate-x-1/2 z-[150] w-full max-w-sm mx-auto px-4
             sm:left-auto sm:right-4 sm:translate-x-0 sm:px-0"
    >
      <div class="bg-gray-900 rounded-2xl shadow-2xl px-4 py-3.5 flex items-center gap-3 border border-white/10">
        <!-- Icono campana -->
        <div class="flex-shrink-0 w-9 h-9 rounded-xl bg-indigo-500/20 flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        </div>

        <!-- Texto -->
        <div class="flex-1 min-w-0">
          <p class="text-sm font-semibold text-white leading-tight">Activar notificaciones</p>
          <p class="text-xs text-gray-400 mt-0.5 leading-snug">{{ subtitle }}</p>
        </div>

        <!-- Botones -->
        <div class="flex items-center gap-1.5 flex-shrink-0">
          <button
            @click="handleDismiss"
            class="text-gray-500 hover:text-gray-300 p-1.5 rounded-lg transition-colors"
            aria-label="Ahora no"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <button
            @click="handleActivate"
            :disabled="isLoading"
            class="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white text-xs font-semibold
                   px-3 py-1.5 rounded-xl transition-colors whitespace-nowrap"
          >
            <span v-if="isLoading" class="flex items-center gap-1.5">
              <svg class="h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Activando...
            </span>
            <span v-else>Activar</span>
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import {
  getPushPermissionState,
  isPushDismissed,
  dismissPushOptIn,
  isFcmEnabled,
  requestAndSavePushToken,
} from '@/utils/fcm';

const props = defineProps({
  userType: { type: String, default: null },
});

const visible = ref(false);
const isLoading = ref(false);
let scheduled = false;

const subtitle = computed(() => {
  if (props.userType === 'agent') return 'Recibe un aviso cuando llegue una nueva solicitud';
  return 'Recibe un aviso cuando tu solicitud avance';
});

function shouldShow(type) {
  if (!isFcmEnabled()) return false;
  if (isPushDismissed()) return false;
  if (getPushPermissionState() === 'granted') return false;
  if (getPushPermissionState() === 'denied') return false;
  if (type !== 'agent' && type !== 'applicant') return false;
  return true;
}

// Esperar a que currentUserType se resuelva tras el login (es asíncrono)
watch(
  () => props.userType,
  async (newType) => {
    if (!isFcmEnabled()) return;
    if (newType !== 'agent' && newType !== 'applicant') return;

    // Permiso ya concedido pero sin token guardado → registrar silenciosamente
    if (getPushPermissionState() === 'granted') {
      await requestAndSavePushToken();
      return;
    }

    if (visible.value || scheduled) return;
    if (!shouldShow(newType)) return;
    scheduled = true;
    setTimeout(() => { visible.value = true; }, 4000);
  },
  { immediate: true }
);

async function handleActivate() {
  isLoading.value = true;
  const result = await requestAndSavePushToken();
  isLoading.value = false;
  visible.value = false;

  if (result === 'denied') {
    dismissPushOptIn(); // No volver a preguntar si denegó en el diálogo del sistema
  }
}

function handleDismiss() {
  visible.value = false;
  dismissPushOptIn();
}
</script>

<style scoped>
.push-optin-enter-active,
.push-optin-leave-active {
  transition: all 0.3s ease;
}
.push-optin-enter-from,
.push-optin-leave-to {
  opacity: 0;
  transform: translate(-50%, 20px);
}
@media (min-width: 640px) {
  .push-optin-enter-from,
  .push-optin-leave-to {
    transform: translateY(20px);
  }
}
</style>
